import { useState } from 'react';
import { TrendingUp, AlertCircle, DollarSign, Calendar, X, Users, BarChart3, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAnalytics, useAppliedInsights, useApplyRecommendation } from '@/hooks/use-analytics';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { MonthlyRevenue, AIInsight, MonthlyDetail } from '@/api/analytics';

interface AnalyticsViewProps {
  propertyId: string;
}

const priorityColors = {
  high: 'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  low: 'bg-gray-100 text-gray-600 border-gray-200',
};

const insightIcons: Record<string, React.ElementType> = {
  'peak-demand': TrendingUp,
  'low-booking': AlertCircle,
  'revenue-success': DollarSign,
  'optimization': Calendar,
};

type DetailPanelType = 
  | { type: 'month'; month: MonthlyRevenue; detail: MonthlyDetail }
  | { type: 'insight'; insight: AIInsight }
  | null;

export function AnalyticsView({ propertyId }: AnalyticsViewProps) {
  const { data: analyticsData, isLoading } = useAnalytics(propertyId);
  const { data: appliedInsightsData } = useAppliedInsights(propertyId);
  const applyRecommendation = useApplyRecommendation(propertyId);
  const [selectedDetail, setSelectedDetail] = useState<DetailPanelType>(null);
  
  const appliedInsights = new Set(appliedInsightsData?.data || []);
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3 p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 !border-[#0f1729]"></div>
        </div>
      </div>
    );
  }
  
  const data = analyticsData?.data;
  if (!data) return null;
  
  // Find max revenue for scaling
  const maxRevenue = Math.max(...data.revenueData.map(d => d.revenue));

  const handleMonthClick = (month: MonthlyRevenue) => {
    const detail = data.monthlyDetails[month.month];
    if (detail) {
      setSelectedDetail({ type: 'month', month, detail });
    }
  };

  const handleInsightClick = (insight: AIInsight) => {
    setSelectedDetail({ type: 'insight', insight });
  };

  const handleApplyRecommendation = (insightId: string) => {
    applyRecommendation.mutate(
      { propertyId, insightId },
      {
        onSuccess: (response) => {
          if (response.success) {
            toast.success(`Recommendation applied! Created: ${response.data.createdRules?.join(', ')}`);
            setSelectedDetail(null);
          } else {
            toast.error(response.error || 'Failed to apply recommendation');
          }
        },
        onError: () => {
          toast.error('Failed to apply recommendation');
        },
      }
    );
  };
  
  return (
    <div className="space-y-6">
      {/* Main Content Grid */}
      <div className={cn(
        "grid gap-6",
        selectedDetail ? "grid-cols-1 lg:grid-cols-3" : "grid-cols-1 lg:grid-cols-2"
      )}>
        {/* Revenue Trend */}
        <Card className="p-6">
          <h2 className="text-lg font-bold font-heading mb-6">Revenue Trend</h2>
          <p className="text-sm text-muted-foreground mb-4">Click on a month for details</p>
          
          <div className="space-y-4">
            {data.revenueData.map((item) => {
              const isSelected = selectedDetail?.type === 'month' && selectedDetail.month.month === item.month;
              return (
                <div 
                  key={item.month} 
                  className={cn(
                    "flex items-center gap-4 p-2 -mx-2 rounded-lg cursor-pointer transition-all hover:bg-muted/50",
                    isSelected && "bg-primary/10 ring-2 ring-primary"
                  )}
                  onClick={() => handleMonthClick(item)}
                >
                  <span className="w-8 text-sm text-muted-foreground font-medium">{item.month}</span>
                  <div className="flex-1 flex items-center gap-3">
                    <div 
                      className={cn(
                        'h-8 rounded-md flex items-center px-3 text-sm font-medium transition-all',
                        item.isPeak ? 'bg-green-500 text-white' : 'bg-accent text-accent-foreground',
                        isSelected && 'ring-2 ring-primary ring-offset-2'
                      )}
                      style={{ width: `${(item.revenue / maxRevenue) * 100}%`, minWidth: '80px' }}
                    >
                      ₹{(item.revenue / 100000).toFixed(1)}L
                    </div>
                    <span className="text-sm text-muted-foreground whitespace-nowrap">
                      {item.occupancy}% occupied
                    </span>
                    {item.isPeak && (
                      <Badge className="bg-green-100 text-green-700 border-green-200 hover:bg-green-100">
                        Peak
                      </Badge>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Summary Stats */}
          <div className="grid grid-cols-2 gap-6 mt-8 pt-6 border-t">
            <div>
              <p className="text-sm text-muted-foreground">Total Revenue</p>
              <p className="text-3xl font-bold font-heading mt-1">
                ₹{(data.totalRevenue / 10000000).toFixed(2)}Cr
              </p>
              <div className="flex items-center gap-1 mt-1 text-sm text-green-600">
                <TrendingUp className="h-3.5 w-3.5" />
                <span>+{data.revenueChange}% vs last period</span>
              </div>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Avg. Occupancy</p>
              <p className="text-3xl font-bold font-heading mt-1">{data.avgOccupancy}%</p>
              <p className="text-sm text-accent mt-1">6-month average</p>
            </div>
          </div>
        </Card>
        
        {/* AI Insights & Recommendations */}
        <Card className="p-6">
          <h2 className="text-lg font-bold font-heading mb-6">AI Insights & Recommendations</h2>
          
          <div className="space-y-4">
            {data.insights.map((insight) => {
              const Icon = insightIcons[insight.type] || TrendingUp;
              const isApplied = appliedInsights.has(insight.id);
              const isSelected = selectedDetail?.type === 'insight' && selectedDetail.insight.id === insight.id;
              
              return (
                <div 
                  key={insight.id} 
                  className={cn(
                    'p-4 rounded-lg border-l-4 cursor-pointer transition-all hover:shadow-md',
                    insight.type === 'revenue-success' && 'bg-amber-50 border-l-amber-400',
                    insight.type === 'peak-demand' && 'bg-green-50 border-l-green-400',
                    insight.type === 'low-booking' && 'bg-orange-50 border-l-orange-400',
                    insight.type === 'optimization' && 'bg-yellow-50 border-l-yellow-400',
                    isSelected && 'ring-2 ring-primary shadow-lg',
                    isApplied && 'opacity-60'
                  )}
                  onClick={() => handleInsightClick(insight)}
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5">
                      {isApplied ? (
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      ) : (
                        <Icon className={cn(
                          'h-5 w-5',
                          insight.type === 'revenue-success' && 'text-amber-600',
                          insight.type === 'peak-demand' && 'text-green-600',
                          insight.type === 'low-booking' && 'text-orange-600',
                          insight.type === 'optimization' && 'text-yellow-600'
                        )} />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-sm">{insight.title}</h3>
                        <Badge variant="outline" className={priorityColors[insight.priority]}>
                          {insight.priority}
                        </Badge>
                        {isApplied && (
                          <Badge className="bg-green-100 text-green-700 border-green-200">
                            Applied
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{insight.description}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground mt-1" />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Detail Panel */}
        {selectedDetail && (
          <Card className="p-6 lg:col-span-1 animate-fade-in">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold font-heading">
                {selectedDetail.type === 'month' ? 'Month Details' : 'Insight Details'}
              </h3>
              <Button variant="ghost" size="icon" onClick={() => setSelectedDetail(null)}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            {selectedDetail.type === 'month' && (
              <div className="space-y-6">
                {/* Month Header */}
                <div>
                  <p className="text-2xl font-bold font-heading">{selectedDetail.month.month} 2024</p>
                  {selectedDetail.month.isPeak && (
                    <Badge className="mt-2 bg-green-100 text-green-700 border-green-200">
                      Peak Month
                    </Badge>
                  )}
                </div>

                {/* Revenue & Occupancy */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg bg-muted/30 border">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Revenue</span>
                    </div>
                    <p className="text-2xl font-bold font-heading">
                      ₹{(selectedDetail.month.revenue / 100000).toFixed(1)}L
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-muted/30 border">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Occupancy</span>
                    </div>
                    <p className="text-2xl font-bold font-heading">{selectedDetail.month.occupancy}%</p>
                  </div>
                </div>

                {/* Detailed Metrics */}
                <div>
                  <h4 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                    <BarChart3 className="h-4 w-4" />
                    Performance Metrics
                  </h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-sm text-muted-foreground">Total Bookings</span>
                      <span className="font-mono font-medium">{selectedDetail.detail.bookings}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-sm text-muted-foreground">Avg. Nightly Rate</span>
                      <span className="font-mono font-medium">₹{selectedDetail.detail.avgNightlyRate.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-sm text-muted-foreground">RevPAR</span>
                      <span className="font-mono font-medium">₹{selectedDetail.detail.revPAR.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-sm text-muted-foreground">Cancellations</span>
                      <span className="font-mono font-medium text-red-600">{selectedDetail.detail.cancellations}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-sm text-muted-foreground">Top Room Type</span>
                      <Badge variant="outline">{selectedDetail.detail.topRoomType}</Badge>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-sm text-muted-foreground">Top Channel</span>
                      <Badge variant="outline">{selectedDetail.detail.topChannel}</Badge>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {selectedDetail.type === 'insight' && (
              <div className="space-y-6">
                {/* Insight Header */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    {(() => {
                      const Icon = insightIcons[selectedDetail.insight.type] || TrendingUp;
                      return <Icon className={cn(
                        'h-6 w-6',
                        selectedDetail.insight.type === 'revenue-success' && 'text-amber-600',
                        selectedDetail.insight.type === 'peak-demand' && 'text-green-600',
                        selectedDetail.insight.type === 'low-booking' && 'text-orange-600',
                        selectedDetail.insight.type === 'optimization' && 'text-yellow-600'
                      )} />;
                    })()}
                    <Badge variant="outline" className={priorityColors[selectedDetail.insight.priority]}>
                      {selectedDetail.insight.priority} priority
                    </Badge>
                  </div>
                  <h4 className="text-xl font-bold font-heading">{selectedDetail.insight.title}</h4>
                </div>

                {/* Description */}
                <div className="p-4 rounded-lg bg-muted/30 border">
                  <p className="text-sm leading-relaxed">{selectedDetail.insight.description}</p>
                </div>

                {/* Recommended Actions */}
                <div>
                  <h4 className="text-sm font-semibold text-muted-foreground mb-3">Recommended Actions</h4>
                  <div className="space-y-2">
                    {selectedDetail.insight.type === 'peak-demand' && (
                      <>
                        <div className="p-3 rounded-lg border bg-card">
                          <p className="text-sm">Increase pricing rules by 10-15%</p>
                        </div>
                        <div className="p-3 rounded-lg border bg-card">
                          <p className="text-sm">Set minimum stay requirements</p>
                        </div>
                      </>
                    )}
                    {selectedDetail.insight.type === 'low-booking' && (
                      <>
                        <div className="p-3 rounded-lg border bg-card">
                          <p className="text-sm">Create promotional discounts</p>
                        </div>
                        <div className="p-3 rounded-lg border bg-card">
                          <p className="text-sm">Enable last-minute deals</p>
                        </div>
                      </>
                    )}
                    {selectedDetail.insight.type === 'revenue-success' && (
                      <>
                        <div className="p-3 rounded-lg border bg-card">
                          <p className="text-sm">Replicate rule to similar events</p>
                        </div>
                        <div className="p-3 rounded-lg border bg-card">
                          <p className="text-sm">Increase multiplier for next occurrence</p>
                        </div>
                      </>
                    )}
                    {selectedDetail.insight.type === 'optimization' && (
                      <>
                        <div className="p-3 rounded-lg border bg-card">
                          <p className="text-sm">Adjust weekend premium rates</p>
                        </div>
                        <div className="p-3 rounded-lg border bg-card">
                          <p className="text-sm">Review occupancy thresholds</p>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Apply Button */}
                {!appliedInsights.has(selectedDetail.insight.id) ? (
                  <Button 
                    className="w-full" 
                    disabled={applyRecommendation.isPending}
                    onClick={() => handleApplyRecommendation(selectedDetail.insight.id)}
                  >
                    {applyRecommendation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                    )}
                    {applyRecommendation.isPending ? 'Applying...' : 'Apply Recommendation'}
                  </Button>
                ) : (
                  <div className="p-4 rounded-lg bg-green-50 border border-green-200 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-600 mx-auto mb-2" />
                    <p className="text-sm font-medium text-green-700">Recommendation Applied</p>
                  </div>
                )}
              </div>
            )}
          </Card>
        )}
      </div>
      
      {/* Rule Performance Summary */}
      <Card className="p-6">
        <h2 className="text-lg font-bold font-heading mb-6">Rule Performance Summary</h2>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="p-4 rounded-lg bg-muted/30 border hover:shadow-md transition-shadow cursor-pointer">
            <p className="text-sm text-muted-foreground">Most Applied Rule</p>
            <p className="text-lg font-bold font-heading mt-2">{data.rulePerformance.mostAppliedRule.name}</p>
            <p className="text-sm text-muted-foreground">{data.rulePerformance.mostAppliedRule.count} times</p>
          </div>
          
          <div className="p-4 rounded-lg bg-muted/30 border hover:shadow-md transition-shadow cursor-pointer">
            <p className="text-sm text-muted-foreground">Highest Revenue Rule</p>
            <p className="text-lg font-bold font-heading mt-2">{data.rulePerformance.highestRevenueRule.name}</p>
            <p className="text-sm text-muted-foreground">₹{(data.rulePerformance.highestRevenueRule.revenue / 100000).toFixed(2)}L</p>
          </div>
          
          <div className="p-4 rounded-lg bg-muted/30 border hover:shadow-md transition-shadow cursor-pointer">
            <p className="text-sm text-muted-foreground">Best Conversion</p>
            <p className="text-lg font-bold font-heading mt-2">{data.rulePerformance.bestConversion.name}</p>
            <p className="text-sm text-muted-foreground">{data.rulePerformance.bestConversion.rate}%</p>
          </div>
          
          <div className="p-4 rounded-lg bg-muted/30 border hover:shadow-md transition-shadow cursor-pointer">
            <p className="text-sm text-muted-foreground">Avg. Price Increase</p>
            <p className="text-lg font-bold font-heading mt-2">+₹{data.rulePerformance.avgPriceIncrease.amount.toLocaleString('en-IN')}</p>
            <p className="text-sm text-muted-foreground">+{data.rulePerformance.avgPriceIncrease.percentage}%</p>
          </div>
        </div>
      </Card>
    </div>
  );
}
